import classNames from "classnames";
import { useRoleProfileActionsPermissions } from "PFApp/hooks/use_role_profile_actions_permissions";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { getProfileName } from "PFCore/helpers/profile";
import { profileHash } from "PFCore/helpers/profile_hash";
import { useIsUnbiasedMode } from "PFCore/hooks/use_is_unbiased_mode";
import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useRoleProfileActionButtonsContext } from "../context/role_profile_action_buttons_context";
import {
  ApprovalButtons,
  BookButton,
  FillAndBookButton,
  FillButton,
  InviteButton,
  ShortlistButton
} from "../parts";
import { ShortlistStateLabel } from "../parts/shortlist_state_label";
import css from "./role_profile_action_buttons.module.scss";

export type ShortlistAction = "accept" | "decline" | "fill" | "invite" | "shortlist";

const GROWL_TTL = 3000;

export type RoleProfileActionButtonsContentProps = {
  customButton?: ReactNode;
  className?: string;
  isOnMatchesTab?: boolean;
};

export const RoleProfileActionButtonsContent = ({
  customButton,
  className,
  isOnMatchesTab = false
}: RoleProfileActionButtonsContentProps) => {
  const { role, profile, shortlist, areButtonsDisabled, removeProfileFromComparison } =
    useRoleProfileActionButtonsContext();
  const isUnbiasedMode = useIsUnbiasedMode(role?.reviewers);
  const { t } = useTranslation("activities");
  const { showGrowl } = useGrowlStoreActions();

  const { checkCanApproveDecline, checkCanInvite, checkCanFill, checkCanBook, checkCanFillAndBook } =
    useRoleProfileActionsPermissions();

  const displayApprovalButtons = checkCanApproveDecline(role, profile, shortlist);
  const displayInviteButton = checkCanInvite(role, profile, shortlist);
  const displayFillButton = checkCanFill(role, profile, shortlist);
  const displayFillAndBookButton = checkCanFillAndBook(role, profile, shortlist);
  const displayBookButton = checkCanBook(role, profile, shortlist);

  const candidateNameDisplay = isUnbiasedMode
    ? t("parts.candidateHash", { profileHash: profileHash(profile.id) })
    : getProfileName(profile);

  const handleShortlistActionSuccess = (action: ShortlistAction) =>
    showGrowl({
      kind: "success",
      message: t(`parts.shortlistActionSuccess.${action}`, { name: candidateNameDisplay }),
      ttl: GROWL_TTL
    });

  const handleShortlistActionError = () =>
    showGrowl({ kind: "error", message: t("parts.problemUpdatingShortlist"), ttl: GROWL_TTL });

  const ShortlistActionButton = useMemo(() => {
    if (displayApprovalButtons) {
      return ApprovalButtons;
    }
    if (displayInviteButton) {
      return InviteButton;
    }
    if (displayFillAndBookButton) {
      return FillAndBookButton;
    }
    if (displayFillButton) {
      return FillButton;
    }
    if (displayBookButton) {
      return BookButton;
    }

    return null;
  }, [
    displayApprovalButtons,
    displayBookButton,
    displayFillAndBookButton,
    displayFillButton,
    displayInviteButton
  ]);

  const isShortlisted = !!shortlist;

  return (
    <div className={classNames(css.root, className)}>
      {(isShortlisted || !!customButton) && (
        <div className={css.labelAndIcons}>
          {isShortlisted && (
            <ShortlistStateLabel
              role={role}
              shortlist={shortlist}
              profile={profile}
              isUndoDisabled={areButtonsDisabled}
              onShortlistDeleteSuccess={removeProfileFromComparison}
              isOnMatchesTab={isOnMatchesTab}
            />
          )}
          {customButton}
        </div>
      )}
      {isShortlisted && !isOnMatchesTab ? (
        ShortlistActionButton && (
          <ShortlistActionButton
            onSuccess={handleShortlistActionSuccess}
            onError={handleShortlistActionError}
          />
        )
      ) : (
        <>
          {!isShortlisted && (
            <ShortlistButton onError={handleShortlistActionError} onSuccess={handleShortlistActionSuccess} />
          )}
          {displayFillAndBookButton && <FillAndBookButton />}
        </>
      )}
    </div>
  );
};
